import { Capacitor } from "@capacitor/core";
import { installNativeKeyboardMotion } from "./nativeKeyboardMotion";

/**
 * Native-shell (Capacitor) quirk fixes — no-ops on the web (SD-11339).
 *
 * Keyboard pan cleanup: even with the Keyboard plugin's resize:body mode,
 * WebKit can leave the WINDOW panned after the keyboard dismisses across a
 * route change — observed live: logging in leaves the app header stranded
 * off-screen above the viewport on the freshly-navigated home page. The app
 * window itself never legitimately scrolls (layout shells are viewport-height
 * with INNER scroll containers), so snapping the window to top whenever the
 * keyboard hides only ever undoes an illegitimate pan.
 */
export const initNativeShellFixes = (): void => {
  if (!Capacitor.isNativePlatform()) return;
  // Both auth and authenticated shells inherit the body's keyboard-resized
  // height instead of using viewport units, which stay unchanged in
  // Keyboard.resize=body mode.
  document.documentElement.classList.add("native-shell");
  if (Capacitor.getPlatform() === "ios") installNativeKeyboardMotion();
  // Pin the zoom level: iOS auto-zooms the viewport when a sub-16px text
  // field gains focus and can leave the page stuck zoomed/clipped after
  // (observed live on the home Ask-AI box). Inside the shell the UI is an
  // app — pinch-zoom isn't expected — and this runs ONLY natively, so the
  // web/PWA keeps full zoom for accessibility.
  document
    .querySelector('meta[name="viewport"]')
    ?.setAttribute(
      "content",
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, viewport-fit=cover",
    );
  // Dynamic import: the plugin stays out of the web bundle entirely.
  import("@capacitor/keyboard")
    .then(({ Keyboard }) => {
      Keyboard.addListener("keyboardDidHide", () => {
        window.scrollTo(0, 0);
      });
    })
    .catch(() => {
      // Plugin unavailable (e.g. web build served inside a stale shell) —
      // the fix is cosmetic; never block startup on it.
    });
};
